import type { CuttingStrategy } from './CuttingStrategy';
import type { IMaterial, DebugStep } from '~/types/Material';
import type { ICutResult } from '~/types/CutResult';
import { he, id } from '@nuxt/ui/runtime/locale/index.js';

interface Rectangle {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface TestResult {
  testId: number;
  description: string;
  wastePercentage: number;
  result: ICutResult;
}






// create array of test gruops 1-3 , 4-9 is a loop using part 2   
function recalculateFreeRectangles(material) {
  const { width: materialWidth, height: materialHeight, children } = material;

  // Start with the full material as one free rectangle
  let freeRectangles = [{
    x: 0,
    y: 0,
    width: materialWidth,
    height: materialHeight
  }];

  // For each placed child, subtract it from existing free rectangles
  children.forEach(child => {
    freeRectangles = subtractRectangleFromFreeRectangles(freeRectangles, child);
  });

  // Remove any overlapping or redundant rectangles
  return optimizeFreeRectangles(freeRectangles);
}

function subtractRectangleFromFreeRectangles(freeRectangles, usedRect) {
  const newFreeRectangles = [];

  freeRectangles.forEach(freeRect => {
    // Check if the used rectangle intersects with this free rectangle
    if (rectanglesIntersect(freeRect, usedRect)) {
      // Split the free rectangle around the used rectangle
      const splits = splitRectangle(freeRect, usedRect);
      newFreeRectangles.push(...splits);
    } else {
      // No intersection, keep the free rectangle as is
      newFreeRectangles.push(freeRect);
    }
  });

  return newFreeRectangles;
}

function rectanglesIntersect(rect1, rect2) {
  return !(rect2.x >= rect1.x + rect1.width ||
    rect2.x + rect2.width <= rect1.x ||
    rect2.y >= rect1.y + rect1.height ||
    rect2.y + rect2.height <= rect1.y);
}

function splitRectangle(freeRect, usedRect) {
  const splits = [];

  // Left split
  if (usedRect.x > freeRect.x) {
    splits.push({
      x: freeRect.x,
      y: freeRect.y,
      width: usedRect.x - freeRect.x,
      height: freeRect.height
    });
  }

  // Right split
  if (usedRect.x + usedRect.width < freeRect.x + freeRect.width) {
    splits.push({
      x: usedRect.x + usedRect.width,
      y: freeRect.y,
      width: freeRect.x + freeRect.width - (usedRect.x + usedRect.width),
      height: freeRect.height
    });
  }

  // Top split
  if (usedRect.y > freeRect.y) {
    splits.push({
      x: freeRect.x,
      y: freeRect.y,
      width: freeRect.width,
      height: usedRect.y - freeRect.y
    });
  }

  // Bottom split
  if (usedRect.y + usedRect.height < freeRect.y + freeRect.height) {
    splits.push({
      x: freeRect.x,
      y: usedRect.y + usedRect.height,
      width: freeRect.width,
      height: freeRect.y + freeRect.height - (usedRect.y + usedRect.height)
    });
  }

  return splits.filter(rect => rect.width > 0 && rect.height > 0);
}

function optimizeFreeRectangles(freeRectangles) {
  // Remove rectangles that are completely contained within others
  return freeRectangles.filter((rect, index) => {
    return !freeRectangles.some((otherRect, otherIndex) => {
      return index !== otherIndex &&
        rect.x >= otherRect.x &&
        rect.y >= otherRect.y &&
        rect.x + rect.width <= otherRect.x + otherRect.width &&
        rect.y + rect.height <= otherRect.y + otherRect.height;
    });
  });
}


export class CompareDebugStrategy implements CuttingStrategy {
  name = 'Debug Test Comparison';
  debugSteps: DebugStep[] = [];
  testResults: TestResult[] = [];
  bestTestId = 0;
  visualizationCounter = 0;
  placedParts: IMaterial[] = [];

  constructor() {
    // No parameters needed
  }


  // ...existing code...

  optimize(material: IMaterial, parts: IMaterial[]): ICutResult | ICutResult[] {
    this.initializeOptimization();

    const partsByType = this.groupPartsByType(parts);
    const allTestCombinations = this.generateTestCombinations(partsByType);
    const sortedParts = this.prepareSortedParts(partsByType);

    this.runAllTests(material, parts, sortedParts, allTestCombinations);

    return this.selectBestResult();
  }

  private initializeOptimization(): void {
    this.testResults = [];
    this.debugSteps = [];
  }

  private generateTestCombinations(partsByType: any): any {
    const parts2 = ref(
      Object.values(partsByType).map(item => ({
        id: item.id,
        width: item.width,
        height: item.height,
        quantity: item.quantity
      }))
    );

    const combinationsData = generateAllPartCombinations(parts2.value);

    // Use the combination data for allTests
    return computed(() => {
      const tests = [];
      let testId = 1;

      combinationsData.forEach((sizeGroup) => {
        sizeGroup.combinations.forEach((combo) => {
          tests.push({
            id: testId++,
            description: combo.description,
            parts: combo.parts,
            partSelections: combo.partSelections,
            selectionSize: combo.selectionSize,
          });
        });
      });

      return tests;
    });
  }

  private prepareSortedParts(partsByType: any): any[] {
    // loop through parts by type and ensure they are sorted by area (largest first)
    const sortedParts = this.getSortedPartTypes(partsByType);
    sortedParts.forEach(partType => {
      partType.parts.sort((a: IMaterial, b: IMaterial) => (b.width * b.height) - (a.width * a.height));
    });

    return sortedParts;
  }

  private runAllTests(
    material: IMaterial,
    parts: IMaterial[],
    sortedParts: any[],
    allTestCombinations: any
  ): void {
    // for loop through sortedParts and log the part types
    // sortedParts.forEach((partType, index) => {
    for (let i = 0; i < allTestCombinations.value.length; i++) {
      const test = { ...allTestCombinations.value[i] };

      const result = this.runTest(
        test.id,
        material,
        parts,
        this.createParts2Array(sortedParts),
        allTestCombinations.value
      );
      this.testResults.push(result);
    }
    // });
  }

  private createParts2Array(sortedParts: any[]): any[] {
    return sortedParts.map(item => ({
      id: item.id,
      width: item.width,
      height: item.height,
      quantity: item.quantity
    }));
  }

  private selectBestResult(): ICutResult | ICutResult[] {
    // Find the test with the lowest waste
    this.testResults.sort((a, b) => a.wastePercentage - b.wastePercentage);

    // Find all tests with the same lowest waste percentage
    const lowestWaste = this.testResults[0].wastePercentage;
    const bestResults = this.testResults.filter(result => result.wastePercentage === lowestWaste);

    if (bestResults.length > 1) {
      // Multiple best results - return array for further comparison
      const tiedResults = bestResults.map(result => {
        const tiedResult = { ...result.result };
        tiedResult.strategyName = `${this.name} - Test ${result.testId} (${(lowestWaste * 100).toFixed(2)}% waste)`;

        tiedResult.debugSteps = tiedResult.debugSteps || [];
        tiedResult.debugSteps.push({
          type: 'check',
          reason: `Tied result: Test ${result.testId} achieved ${(lowestWaste * 100).toFixed(2)}% waste`,
          visualizationId: `tied-result-${result.testId}`
        });

        return tiedResult;
      });

      return tiedResults;
    } else {
      // Single best result
      this.bestTestId = this.testResults[0].testId;
      const bestResult = this.testResults[0].result;
      bestResult.strategyName = `${this.name} - Best is Test ${this.bestTestId} (${(this.testResults[0].wastePercentage * 100).toFixed(2)}% waste)`;

      bestResult.debugSteps = bestResult.debugSteps || [];
      bestResult.debugSteps.push({
        type: 'check',
        reason: 'Test Comparison Results',
        visualizationId: 'test-comparison'
      });

      return bestResult;
    }
  }

  // ...existing code...

  runTest(testId: number, material: IMaterial, parts: IMaterial[], partType: IMaterial[], allTests: any): TestResult {
    const clonedMaterial = this.cloneMaterial(material);


    // get specific test object from allTests
    const test = allTests.find(t => t.id === testId);
    const partTypeNumber = test.partSelections.length;
    let description = test.description || `Test ${testId}`;

    // Initialize material for this test
    const resultMaterial: IMaterial = {
      ...clonedMaterial,
      id: `material-debug-${testId}`,
      children: [],
      debugSteps: []
    };

    // Group parts by dimensions for easy access
    const partsByType = this.groupPartsByType(parts);

    // loop through parts by type and ensure they are sorted by area (largest first)
    const sortedParts = this.getSortedPartTypes(partsByType);
    sortedParts.forEach(partType => {
      partType.parts.sort((a: IMaterial, b: IMaterial) => (b.width * b.height) - (a.width * a.height));
    });
    let newTestId = 0
    let testType = 0
    let partNumberTest = 0
    if (testId - 1 < partType.length * 3) {
      // For tests 1-3, use the partTypeNumber directly
      testType = 1
      newTestId = testId % 3; // Ensure testId is between 1 and 3
      if (newTestId === 0) {
        newTestId = 3; // Adjust to ensure we have a valid testId
      }
      // testid bagi 3 , round down, tambah 1
      partNumberTest = Math.floor((testId - 1) / 3) + 1; // Calculate part number based on testId
    } else {
      testType = 2
      newTestId = testId - partTypeNumber * 3
    }
    const compositeKey = `${testType}-${newTestId}`;

    // Run the appropriate test based on testId
    if (compositeKey.startsWith("1-")) {
      switch (compositeKey) {
        case "1-1":
          this.runTest1(resultMaterial, parts, partsByType, partNumberTest);
          break;
        case "1-2":
          this.runTest2(resultMaterial, parts, partsByType, partNumberTest);
          break;
        case "1-3":
          this.runTest3(resultMaterial, parts, partsByType, partNumberTest);
          break;
      }
    } else if (compositeKey.startsWith("2-")) {
      // All 2-x cases use the same method
      this.runTest21(resultMaterial, parts, partsByType, partNumberTest, test);
    } else {
      // For tests 4-21, just create a placeholder implementation
      this.runPlaceholderTest(testId, resultMaterial, parts, partsByType);
    }

    // Calculate waste percentage
    const totalArea = material.width * material.height;
    const usedArea = (resultMaterial.children || [])
      .reduce((sum, part) => sum + (part.width * part.height), 0);
    const wastePercentage = 1 - (usedArea / totalArea);

    return {
      testId,
      description,
      wastePercentage,
      result: {
        usedMaterials: [resultMaterial],
        wastePercentage,
        strategyName: `Debug Test ${testId}: ${description}`,
        debugSteps: resultMaterial.debugSteps || []
      }
    };
  }
  private removeAlreadyPlacedParts(material: IMaterial, partTypes: any[]): any[] {
    if (!material.children || material.children.length === 0) {
      return partTypes; // No parts placed, return original partTypes
    }

    // Get IDs of all placed parts
    const placedPartIds = new Set(material.children.map(child => child.id));

    // Create new partTypes array with unplaced parts only
    const updatedPartTypes = partTypes.map(partType => {
      const unplacedParts = partType.parts.filter(part => !placedPartIds.has(part.id));

      return {
        ...partType,
        parts: unplacedParts,
        quantity: unplacedParts.length
      };
    }).filter(partType => partType.parts.length > 0); // Remove part types with no remaining parts

    return updatedPartTypes;
  }

  // TEST 1: All Part 1 in landscape orientation
  runTest1(material: IMaterial, parts: IMaterial[], partsByType: any, partTypeNumber: number): void {
    // Implementation for Test 1 - All parts in landscape orientation
    const partTypes = this.getSortedPartTypes(partsByType);
    if (partTypes.length === 0) return;

    const part1 = partTypes[partTypeNumber - 1];
    const freeRectangles: Rectangle[] = [
      { x: 0, y: 0, width: material.width, height: material.height }
    ];

    // Add initial visualization of free rectangles
    this.addFreeRectanglesVisualization(material, freeRectangles, "Initial free rectangle");

    // Place Part 1 in landscape orientation
    for (let i = 0; i < part1.parts.length; i++) {
      const currentPart = { ...part1.parts[i] };
      const result = this.findPositionForRectangle(freeRectangles, currentPart.width, currentPart.height);

      if (result.found) {
        const { rectIndex, x, y } = result;

        // Add part to material
        const placedPart: IMaterial = {
          ...currentPart,
          x,
          y,
          rotated: false
        };

        material.children = material.children || [];
        material.children.push(placedPart);

        // Add debug step
        material.debugSteps = material.debugSteps || [];
        material.debugSteps.push({
          type: 'place',
          part: placedPart,
          position: { x, y }
        });

        // Update free rectangles
        if (rectIndex !== undefined && rectIndex >= 0) {
          const rect = freeRectangles[rectIndex];
          this.splitFreeRectangle(material, freeRectangles, rectIndex, rect, placedPart);
        }
      }
    }

    // After all part1 are placed, try to fill with remaining parts
    const remainingPartTypes = this.removeAlreadyPlacedParts(material, partTypes);

    this.fillRemainingSpace(material, freeRectangles, remainingPartTypes);
  }

  // TEST 2: All Part 1 in portrait orientation
  runTest2(material: IMaterial, parts: IMaterial[], partsByType: any, partTypeNumber: number): void {
    // Implementation for Test 2 - All parts in portrait orientation
    const partTypes = this.getSortedPartTypes(partsByType);
    if (partTypes.length === 0) return;

    const part1 = partTypes[partTypeNumber - 1];
    const freeRectangles: Rectangle[] = [
      { x: 0, y: 0, width: material.width, height: material.height }
    ];

    // Place Part 1 in portrait orientation
    for (let i = 0; i < part1.parts.length; i++) {
      const currentPart = { ...part1.parts[i] };
      // Swap width and height for portrait orientation
      const result = this.findPositionForRectangle(freeRectangles, currentPart.height, currentPart.width);

      if (result.found) {
        const { rectIndex, x, y, rotated } = result;

        // Add part to material with swapped dimensions
        const placedPart: IMaterial = {
          ...currentPart,
          width: currentPart.height,
          height: currentPart.width,
          x,
          y,
          rotated: true
        };

        material.children = material.children || [];
        material.children.push(placedPart);

        // Add debug step
        material.debugSteps = material.debugSteps || [];
        material.debugSteps.push({
          type: 'place',
          part: placedPart,
          position: { x, y }
        });

        // Update free rectangles
        if (rectIndex !== undefined && rectIndex >= 0) {
          const rect = freeRectangles[rectIndex];
          this.splitFreeRectangle(material, freeRectangles, rectIndex, rect, placedPart);
        }
      }
    }

    const remainingPartTypes = this.removeAlreadyPlacedParts(material, partTypes);
    // After all part1 are placed, try to fill with remaining parts
    this.fillRemainingSpace(material, freeRectangles, remainingPartTypes)
  }

  // TEST 3: Alternating orientations for Part 1
  runTest3(material: IMaterial, parts: IMaterial[], partsByType: any, partTypeNumber: number): void {
    // 'Place Part 1 in landscape for first row, portrait for second row, and alternate for subsequent rows\nAfter placing all Part 1 pieces, identify and fill remaining free spaces with Part 2, then Part 3',
    // Implementation for Test 3 - Alternating orientations
    const partTypes = this.getSortedPartTypes(partsByType);
    if (partTypes.length === 0) return;

    const part1 = partTypes[partTypeNumber - 1];
    const freeRectangles: Rectangle[] = [
      { x: 0, y: 0, width: material.width, height: material.height }
    ];

    // Track the current row
    let currentRow = 0;
    let rowX = 0;
    let rowY = 0;
    let rowHeight = 0;
    let isLandscape = true; // Start with landscape
    const unusedSpaceTracker = [];


    // Place parts with alternating orientations
    for (let i = 0; i < part1.parts.length; i++) {
      const currentPart = { ...part1.parts[i] };

      // Determine orientation based on row
      let width = isLandscape ? currentPart.width : currentPart.height;
      let height = isLandscape ? currentPart.height : currentPart.width;

      // Check if we need to start a new row
      if (rowX + width > material.width) {
        // row is full, move to next row
        // Create new array to track the unused space at the end of each row, fields : 1. row , 2 .column, 3. usespace for the row , and 4. unused space
        if (rowX > 0) {  // Only create unused space if we placed at least one part
          const unusedRect: Rectangle = {
            x: rowX,
            y: rowY,
            width: material.width - rowX,
            height: rowHeight
          };
          unusedSpaceTracker.push({
            row: currentRow,
            column: width, // This is the ending position
            usedSpace: rowX * height,
            unusedSpace: (material.width - rowX) * height,
            wastePercentage: (material.width - rowX) * height / (material.width * height),
            rotate: isLandscape
          });

          // Add this to free rectangles if it has sufficient size
          // if (unusedRect.width > 0 && unusedRect.height > 0) {
          //   freeRectangles.push(unusedRect);
          // }
        }
        const unusedRowSpace = (material.width - rowX) * rowHeight;

        // Add this unusable space as a debug step
        material.debugSteps = material.debugSteps || [];
        material.debugSteps.push({
          type: 'check',
          reason: `Row ${currentRow} ended with ${unusedRowSpace} unused space`,
          visualizationId: `row-${currentRow}-unused`
        });

        rowX = 0;
        rowY += rowHeight;
        rowHeight = 0;
        currentRow++;
        if (currentRow >= 2) {
          // check on unusedSpaceTracker which one waste percentage is the lowest
          let lowestWaste = Infinity;
          let bestRowIndex = -1;
          for (let j = 0; j < unusedSpaceTracker.length; j++) {
            if (unusedSpaceTracker[j].wastePercentage < lowestWaste) {
              lowestWaste = unusedSpaceTracker[j].wastePercentage;
              bestRowIndex = j;
            }
          }
          isLandscape = unusedSpaceTracker[bestRowIndex].rotate; // Use the orientation of the best row
          // if (bestRowIndex !== -1) {
          // isLandscape = unusedSpaceTracker; // Alternate orientation for next row
        } else {

          isLandscape = !isLandscape; // Alternate orientation for next row
          const tempWidth = currentPart.width;
          width = currentPart.height;
          height = tempWidth;
        }
        // Alternate orientation for the next row means we need to swap width and height
        const tempWidth = currentPart.width;
        const tempHeight = currentPart.height;
        // if (isLandscape) { // Landscape orientation width is greater than height
        if (isLandscape) {
          // check which one is greater
          if (tempWidth > tempHeight) {
            width = tempWidth;
            height = tempHeight;
          } else {
            width = tempHeight;
            height = tempWidth;
          }
        }
        else // portrait orientation height is greater than width 
        {
          if (tempWidth > tempHeight) {
            width = tempHeight;
            height = tempWidth;
          } else {
            width = tempWidth;
            height = tempHeight;
          }
        }

      }

      // Find position in current row
      let x = rowX;
      let y = rowY;
      const rectIndex = this.findRectangleContaining(freeRectangles, x, y);

      if (rectIndex !== -1 && x + width <= material.width && y + height <= material.height) {
        // Add part to material
        const placedPart: IMaterial = {
          ...currentPart,
          width,
          height,
          x,
          y,
          rotated: isLandscape,
          row: currentRow,
          column: Math.floor(rowX / width) // Calculate column based on rowX
        };

        material.children = material.children || [];
        material.children.push(placedPart);

        // Add debug step
        material.debugSteps = material.debugSteps || [];
        material.debugSteps.push({
          type: 'place',
          part: placedPart,
          position: { x, y }
        });

        // Update free rectangles
        const rect = freeRectangles[rectIndex];
        this.splitFreeRectangle(material, freeRectangles, rectIndex, rect, placedPart);

        // Update row information
        rowX += width;
        rowHeight = Math.max(rowHeight, height);
      }
    }

    const remainingPartTypes = this.removeAlreadyPlacedParts(material, partTypes);
    // After all part1 are placed, try to fill with remaining parts
    this.fillRemainingSpace(material, freeRectangles, remainingPartTypes)
  }


  runTest21(material: IMaterial, parts: IMaterial[], partsByType: any, partTypeNumber: number, test: any): void {
    // karena ini test 21
    // jadi posisi landscape & landscape 
    const filteredPartsByType = Object.fromEntries(
      Object.entries(partsByType).filter(([key, value]) =>
        test.parts.some(part => {
          const landscapeKey = `${part.width}x${part.height}`;
          const portraitKey = `${part.height}x${part.width}`;
          return key === landscapeKey || key === portraitKey;
        })
      )
    );
    const partTypes = this.getSortedPartTypes(filteredPartsByType);
    if (partTypes.length === 0) return;


    for (let i = 0; i < partTypes.length; i++) {
      const partType = partTypes[i];
      const partsOfType = parts.filter(part =>
        part.width === partType.width && part.height === partType.height
      );

      // Store the parts for this type to be used in the placement logic below
      if (partsOfType.length > 0) {
        partsByType[`${partType.width}x${partType.height}`] = partsOfType;
      }
    }

    const part1 = partTypes[partTypeNumber - 1];
    const freeRectangles: Rectangle[] = [
      { x: 0, y: 0, width: material.width, height: material.height }
    ];

    // Track the current row
    let currentRow = 0;
    let rowX = 0;
    let rowY = 0;
    let rowHeight = 0;
    let isLandscape = true; // Start with landscape
    const unusedSpaceTracker = [];


    // Loop through all part types
    let currentY = 0; // Track vertical position across all part types
    let totalPlacedParts = 0;
    const wastePercentageInRow: { [key: number]: number } = {}; // Array to store waste percentage by row number

    for (let partTypeIndex = 0; partTypeIndex < partTypes.length; partTypeIndex++) {
      const partOrientation = test.parts[partTypeIndex].orientation
      const currentPartType = partTypes[partTypeIndex];
      if (!currentPartType || !currentPartType.parts || currentPartType.parts.length === 0) continue;

      // Set all parts to landscape orientation for this part type
      let landscapeWidth = currentPartType.width;
      let landscapeHeight = currentPartType.height;
      if (partOrientation == 'landscape') {
        landscapeWidth = Math.max(currentPartType.width, currentPartType.height);
        landscapeHeight = Math.min(currentPartType.width, currentPartType.height);
      } else {
        landscapeWidth = Math.min(currentPartType.width, currentPartType.height);
        landscapeHeight = Math.max(currentPartType.width, currentPartType.height);

      }

      // Calculate how many parts can fit in one row for this part type
      const partsPerRow = Math.floor(material.width / landscapeWidth);
      const actualPartsToPlace = Math.min(currentPartType.parts.length, partsPerRow);

      // Check if there's enough height remaining
      if (currentY + landscapeHeight > material.height) {
        console.log(`Not enough height remaining for part type ${partTypeIndex + 1}`);
        break;
      }

      // Place parts in one row for this part type using freeRectangles
      let placedParts = 0;
      for (let col = 0; col < partsPerRow && placedParts < actualPartsToPlace; col++) {
        const x = col * landscapeWidth;
        const y = currentY;

        // Find position using freeRectangles
        const rectIndex = this.findRectangleContaining(freeRectangles, x, y);

        if (rectIndex !== -1 && x + landscapeWidth <= material.width && y + landscapeHeight <= material.height) {
          // Place the part
          const partToPlace = currentPartType.parts[placedParts];
          const placedPart = {
            id: partToPlace.id || `part-${totalPlacedParts}`,
            width: landscapeWidth,
            height: landscapeHeight,
            x: x,
            y: y,
            type: partToPlace.type || 'part',
            originalWidth: partToPlace.width,
            originalHeight: partToPlace.height,
            orientation: 'landscape',
            rotated: true,
            row: partTypeIndex,
            column: col
          };

          material.children.push(placedPart);

          // Add debug step
          material.debugSteps = material.debugSteps || [];
          material.debugSteps.push({
            type: 'place',
            part: placedPart,
            position: { x, y }
          });

          // Update free rectangles using splitFreeRectangle
          const rect = freeRectangles[rectIndex];
          this.splitFreeRectangle(material, freeRectangles, rectIndex, rect, placedPart);

          placedParts++;
          totalPlacedParts++;
        }
      }

      // calculate waste percentage for this row
      const rowArea = material.width * landscapeHeight;
      const usedAreaInRow = placedParts * (landscapeWidth * landscapeHeight);
      const wasteAreaInRow = rowArea - usedAreaInRow;
      const wastePercentageInRowValue = (wasteAreaInRow / rowArea) * 100;

      // Store waste percentage in array with row number as key
      wastePercentageInRow[partTypeIndex] = wastePercentageInRowValue;

      // Track unused space at end of this row
      if (placedParts * landscapeWidth < material.width) {
        const unusedWidth = material.width - (placedParts * landscapeWidth);
        const unusedRect: Rectangle = {
          x: placedParts * landscapeWidth,
          y: currentY,
          width: unusedWidth,
          height: landscapeHeight
        };

        // Add unused space to freeRectangles if it has sufficient size
        if (unusedRect.width > 0 && unusedRect.height > 0) {
          freeRectangles.push(unusedRect);
        }

        // Add debug step for unused space
        material.debugSteps = material.debugSteps || [];
        material.debugSteps.push({
          type: 'check',
          reason: `Row ${partTypeIndex} ended with ${unusedWidth * landscapeHeight} unused space`,
          visualizationId: `row-${partTypeIndex}-unused`
        });
      }


      // Move to next row
      currentY += landscapeHeight;
    }

    // Calculate remaining space after processing all part types
    const usedHeight = currentY;
    const remainingHeight = material.height - usedHeight;
    const remainingArea = material.width * remainingHeight;


    // Update unused space tracker
    if (remainingHeight > 0) {
      unusedSpaceTracker.push({
        x: 0,
        y: usedHeight,
        width: material.width,
        height: remainingHeight,
        area: remainingArea
      });
    }
    // choose which row has the lowest waste percentage
    let bestRowIndex = -1;
    let lowestWastePercentage = Infinity;
    for (const rowIndex in wastePercentageInRow) {
      const wastePercentage = wastePercentageInRow[rowIndex];
      if (wastePercentage < lowestWastePercentage) {
        lowestWastePercentage = wastePercentage;
        bestRowIndex = parseInt(rowIndex, 10);
      }
    }


    //    - Get the part type for bestRowIndex
    const bestPartType = partTypes[bestRowIndex];
    if (bestPartType) {
      //    - Calculate how much horizontal space is still available
      const bestLandscapeWidth = Math.max(bestPartType.width, bestPartType.height);
      const bestLandscapeHeight = Math.min(bestPartType.width, bestPartType.height);
      const partsAlreadyInBestRow = Math.floor(material.width / bestLandscapeWidth);
      const usedWidthInBestRow = partsAlreadyInBestRow * bestLandscapeWidth;
      const availableWidthInBestRow = material.width - usedWidthInBestRow;


      const remainingParts = bestPartType.parts.slice(partsAlreadyInBestRow);
      const additionalPartsThatCanFit = Math.floor(availableWidthInBestRow / bestLandscapeWidth);
      const actualAdditionalParts = Math.min(remainingParts.length, additionalPartsThatCanFit);

      // 4. Place parts in the identified optimal row using freeRectangles
      for (let i = 0; i < actualAdditionalParts; i++) {
        //    - Position parts horizontally after existing parts
        const x = usedWidthInBestRow + (i * bestLandscapeWidth);
        const y = bestRowIndex * bestLandscapeHeight;
        //    - Use the same landscape orientation as the row
        //    - Update x coordinates based on existing parts count

        // Find position using freeRectangles
        const rectIndex = this.findRectangleContaining(freeRectangles, x, y);

        if (rectIndex !== -1 && x + bestLandscapeWidth <= material.width && y + bestLandscapeHeight <= material.height) {
          const partToPlace = remainingParts[i];
          const placedPart = {
            id: partToPlace.id || `additional-part-${totalPlacedParts}`,
            width: bestLandscapeWidth,
            height: bestLandscapeHeight,
            x: x,
            y: y,
            type: partToPlace.type || 'part',
            originalWidth: partToPlace.width,
            originalHeight: partToPlace.height,
            orientation: 'landscape',
            rotated: true,
            row: bestRowIndex,
            column: partsAlreadyInBestRow + i
          };

          // 5. Update the material layout and waste calculations
          //    - Add placed parts to material.children
          material.children?.push(placedPart);

          // Add debug step
          material.debugSteps = material.debugSteps || [];
          material.debugSteps.push({
            type: 'place',
            part: placedPart,
            position: { x, y }
          });

          // Update free rectangles using splitFreeRectangle
          const rect = freeRectangles[rectIndex];
          this.splitFreeRectangle(material, freeRectangles, rectIndex, rect, placedPart);

          //    - Update totalPlacedParts counter
          totalPlacedParts++;
        }
      }

      //    - Recalculate waste percentage for the updated row
      if (actualAdditionalParts > 0) {
        const newPartsInRow = partsAlreadyInBestRow + actualAdditionalParts;
        const newUsedAreaInRow = newPartsInRow * (bestLandscapeWidth * bestLandscapeHeight);
        const rowArea = material.width * bestLandscapeHeight;
        const newWasteAreaInRow = rowArea - newUsedAreaInRow;
        const newWastePercentageInRow = (newWasteAreaInRow / rowArea) * 100;

        // Update the waste percentage array
        wastePercentageInRow[bestRowIndex] = newWastePercentageInRow;

        // Track unused space at end of row
        if (usedWidthInBestRow + (actualAdditionalParts * bestLandscapeWidth) < material.width) {
          const unusedWidth = material.width - (usedWidthInBestRow + (actualAdditionalParts * bestLandscapeWidth));
          const unusedRect: Rectangle = {
            x: usedWidthInBestRow + (actualAdditionalParts * bestLandscapeWidth),
            y: bestRowIndex * bestLandscapeHeight,
            width: unusedWidth,
            height: bestLandscapeHeight
          };

          // Add unused space to tracker
          if (unusedRect.width > 0 && unusedRect.height > 0) {
            freeRectangles.push(unusedRect);
          }
        }

        // console.log(`Updated row ${bestRowIndex}: Added ${actualAdditionalParts} parts, new waste: ${newWastePercentageInRow.toFixed(2)}%`);
      }
    }

    // After all part1 are placed, try to fill with remaining parts
    const remainingPartTypes = this.removeAlreadyPlacedParts(material, partTypes);
    this.fillRemainingSpace(material, freeRectangles, remainingPartTypes);
  }

  // Placeholder implementation for tests 4-21
  runPlaceholderTest(testId: number, material: IMaterial, parts: IMaterial[], partsByType: any): void {
    // For placeholder tests, we'll modify the waste percentage based on test number
    // to show differences in the comparison, but use a simple packing algorithm

    // Get unique part types
    const partTypes = this.getSortedPartTypes(partsByType);
    if (partTypes.length === 0) return;

    // Determine which type of parts to start with based on test ID
    let firstPartIndex = 0;
    if (testId >= 4 && testId <= 6) {
      firstPartIndex = Math.min(1, partTypes.length - 1); // Use part type 2 if available
    } else if (testId >= 7 && testId <= 9) {
      firstPartIndex = Math.min(2, partTypes.length - 1); // Use part type 3 if available
    }

    // Reorder part types based on test
    const reorderedTypes = [...partTypes];
    if (firstPartIndex < reorderedTypes.length) {
      const temp = reorderedTypes[0];
      reorderedTypes[0] = reorderedTypes[firstPartIndex];
      reorderedTypes[firstPartIndex] = temp;
    }

    // Simple packing - left to right, top to bottom
    const freeRectangles: Rectangle[] = [
      { x: 0, y: 0, width: material.width, height: material.height }
    ];

    // Pack all parts using a simple algorithm
    for (const partType of reorderedTypes) {
      for (const part of partType.parts) {
        // Try both orientations
        let bestFit = this.findPositionForRectangle(freeRectangles, part.width, part.height);
        let rotated = false;

        // If landscape doesn't fit, try portrait
        if (!bestFit.found) {
          const portraitFit = this.findPositionForRectangle(freeRectangles, part.height, part.width);
          if (portraitFit.found) {
            bestFit = portraitFit;
            rotated = true;
          }
        }

        if (bestFit.found) {
          const { rectIndex, x, y } = bestFit;

          // Add part to material
          const placedPart: IMaterial = {
            ...part,
            width: rotated ? part.height : part.width,
            height: rotated ? part.width : part.height,
            x,
            y,
            rotated
          };

          material.children = material.children || [];
          material.children.push(placedPart);

          // Update free rectangles
          if (rectIndex !== undefined && rectIndex >= 0) {
            const rect = freeRectangles[rectIndex];
            this.splitFreeRectangle(material, freeRectangles, rectIndex, rect, placedPart);
          }
        }
      }
    }

    // For demonstration, adjust the layout slightly based on test number to show different results
    // In a real implementation, you would implement the actual algorithms from your manual
    if (material.children && material.children.length > 0) {
      // Apply a test-specific transformation to simulate different algorithms
      const adjustment = (testId % 5) * 0.02; // Creates a small variation
      if (testId > 9) {
        // Simulate different results for tests 10+
        // This gives each test a slightly different waste percentage
        const firstChild = material.children[0];
        if (firstChild) {
          firstChild.x = (firstChild.x || 0) + adjustment * 10;
          firstChild.y = (firstChild.y || 0) + adjustment * 5;
        }
      }
    }
  }


  // Helper method to fill remaining space with smaller parts
  fillRemainingSpace(material: IMaterial, freeRectangles: Rectangle[], remainingPartTypes: any[]): void {
    // Sort remaining part types by area (smallest first) to better fill small spaces
    // recalculate freeRectangles from material children
    // freeRectangles = recalculateFreeRectangles(material);
    // if (!material.children || material.children.length === 0) {
    //   return; // No parts placed, nothing to fill
    // }
    const sortedTypes = [...remainingPartTypes].sort((a, b) =>
      (a.width * a.height) - (b.width * b.height)
    );

    let placed = true;
    while (placed && freeRectangles.length > 0) {
      placed = false;

      // Try each part type
      for (const partType of sortedTypes) {
        // Try each remaining part of this type
        for (let i = 0; i < partType.parts.length; i++) {
          const part = partType.parts[i];

          // Skip parts that are already placed
          if (part.placed) continue;

          // Try to find a position for this part
          let result = this.findPositionForRectangle(freeRectangles, part.width, part.height, true);
          let rotated = false;

          // If it doesn't fit, try rotated
          if (!result.found) {
            result = this.findPositionForRectangle(freeRectangles, part.height, part.width, true);
            rotated = true;
          }

          if (result.found) {
            const { rectIndex, x, y } = result;

            // Add part to material
            const placedPart: IMaterial = {
              ...part,
              width: rotated ? part.height : part.width,
              height: rotated ? part.width : part.height,
              x,
              y,
              rotated
            };

            material.children = material.children || [];
            material.children.push(placedPart);

            // Mark as placed
            part.placed = true;

            // Add debug step
            material.debugSteps = material.debugSteps || [];
            material.debugSteps.push({
              type: 'place',
              part: placedPart,
              position: { x, y },
              reason: 'Fill remaining space'
            });

            // Update free rectangles
            if (rectIndex !== undefined && rectIndex >= 0) {
              const rect = freeRectangles[rectIndex];
              this.splitFreeRectangle(material, freeRectangles, rectIndex, rect, placedPart);
            }

            placed = true;
            break; // Move to the next part type
          }
        }

        if (placed) break; // Restart with smallest parts
      }
    }
  }

  // Helper method to group parts by type
  groupPartsByType(parts: IMaterial[]): any {
    const result: { [key: string]: any } = {};
    let i = 0;

    parts.forEach(part => {
      const key = `${part.width}x${part.height}`;
      if (!result[key]) {
        result[key] = {
          id: `part-${i++}`,
          width: part.width,
          height: part.height,
          parts: []
        };
      }
      result[key].parts.push({ ...part, placed: false });
      result[key].quantity = result[key].parts.length;
    });

    return result;
  }

  // Helper method to get part types sorted by area
  getSortedPartTypes(partsByType: any): any[] {
    return Object.values(partsByType).sort((a: any, b: any) =>
      (b.width * b.height) - (a.width * a.height)
    );
  }

  // Helper method to find a position for a rectangle
  findPositionForRectangle(freeRectangles: Rectangle[], width: number, height: number, fillRemainingSpace = false): {
    found: boolean;
    rectIndex?: number;
    x?: number;
    y?: number;
    rotated?: boolean;
  } {
    // Try to find a free rectangle that fits
    for (let i = 0; i < freeRectangles.length; i++) {
      const rect = freeRectangles[i];
      if (rect.width >= width && rect.height >= height) {
        return {
          found: true,
          rectIndex: i,
          x: rect.x,
          y: rect.y,
          rotated: false
        };
      }
      // else if (rect.width >= height && rect.height >= width) {
      //   // If it fits rotated, return that position
      //   return {
      //     found: true,
      //     rectIndex: i,
      //     x: rect.x,
      //     y: rect.y,
      //     rotated: true
      //   };
      // }
    }

    return { found: false };
  }

  // Helper method to find rectangle containing point
  findRectangleContaining(freeRectangles: Rectangle[], x: number, y: number): number {
    return freeRectangles.findIndex(rect =>
      x >= rect.x &&
      x < rect.x + rect.width &&
      y >= rect.y &&
      y < rect.y + rect.height
    );
  }

  // Helper method to split a free rectangle when a part is placed
  splitFreeRectangle(material: IMaterial, freeRectangles: Rectangle[], rectIndex: number, rect: Rectangle, part: IMaterial): void {
    // Remove the original rectangle
    // freeRectangles.splice(rectIndex, 1);

    const placed: Rectangle = {
      x: part.x || 0,
      y: part.y || 0,
      width: part.width,
      height: part.height
    };

    // Create new rectangles based on the placement
    const newRectangles: Rectangle[] = [];

    for (let i = 0; i < freeRectangles.length; i++) {
      const freeRect = freeRectangles[i];

      // Skip if this free rectangle doesn't intersect with the placed rectangle
      if (freeRect.x >= placed.x + placed.width || freeRect.y >= placed.y + placed.height ||
        freeRect.x + freeRect.width <= placed.x || freeRect.y + freeRect.height <= placed.y) {
        continue;
      }

      // Right of the placed rectangle
      if (freeRect.x + freeRect.width > placed.x + placed.width) {
        newRectangles.push({
          x: placed.x + placed.width,
          y: freeRect.y,
          width: freeRect.x + freeRect.width - (placed.x + placed.width),
          height: freeRect.height
        });
      }

      // Below the placed rectangle
      if (freeRect.y + freeRect.height > placed.y + placed.height) {
        newRectangles.push({
          x: freeRect.x,
          y: placed.y + placed.height,
          width: freeRect.width,
          height: freeRect.y + freeRect.height - (placed.y + placed.height)
        });
      }

      // Mark this rectangle for removal
      freeRectangles[i] = { x: -1, y: -1, width: 0, height: 0 };
    }

    // Remove marked rectangles
    for (let i = freeRectangles.length - 1; i >= 0; i--) {
      if (freeRectangles[i].width === 0 || freeRectangles[i].height === 0) {
        freeRectangles.splice(i, 1);
      }
    }

    // Add new rectangles
    freeRectangles.push(...newRectangles);

    // Optimize rectangles by removing contained ones
    this.optimizeFreeRectangles(freeRectangles);

    // Visualize the free rectangles after splitting
    this.addFreeRectanglesVisualization(
      material,
      freeRectangles,
      `After placing part at (${placed.x},${placed.y}) of size ${placed.width}x${placed.height}`,
      part
    );
  }

  private optimizeFreeRectangles(freeRectangles: Rectangle[]): void {
    // Remove rectangles that are contained within others
    for (let i = freeRectangles.length - 1; i >= 0; i--) {
      const rect1 = freeRectangles[i];

      for (let j = 0; j < freeRectangles.length; j++) {
        if (i === j) continue;

        const rect2 = freeRectangles[j];

        if (rect2.x <= rect1.x && rect2.y <= rect1.y &&
          rect2.x + rect2.width >= rect1.x + rect1.width &&
          rect2.y + rect2.height >= rect1.y + rect1.height) {

          freeRectangles.splice(i, 1);
          break;
        }
      }
    }
  }

  // Helper method to clone a material
  cloneMaterial(material: IMaterial): IMaterial {
    return {
      ...material,
      children: material.children ? [...material.children] : undefined,
      debugSteps: material.debugSteps ? [...material.debugSteps] : undefined
    };
  }

  private addFreeRectanglesVisualization(
    material: IMaterial,
    freeRectangles: Rectangle[], reason: string, placedPart?: IMaterial,): void {
    const visualizationId = `freeRect-${++this.visualizationCounter}`;

    material.debugSteps = material.debugSteps || [];
    material.debugSteps.push({
      type: 'freeRectangles',
      reason,
      freeRectangles: [...freeRectangles], // Clone to prevent further modifications
      placedParts: [...this.placedParts], // Add all placed parts up to this point
      visualizationId
    });


    // this.debugSteps.push({
    //   type: 'freeRectangles',
    //   reason,
    //   freeRectangles: [...freeRectangles], // Clone to prevent further modifications
    //   placedParts: [...this.placedParts], // Add all placed parts up to this point
    //   visualizationId
    // } as DebugStep);
  }

  // Helper method to get test description
  // getTestDescription(testId: number): string {
  //   const descriptions: { [key: number]: string } = {
  //     1: 'All Part 1 in landscape orientation',
  //     2: 'All Part 1 in portrait orientation',
  //     3: 'Part 1 alternating orientations by row',
  //     4: 'Part 2 in landscape orientation',
  //     5: 'Part 2 in portrait orientation',
  //     6: 'Part 2 alternating orientations',
  //     7: 'Part 3 in landscape orientation',
  //     8: 'Part 3 in portrait orientation',
  //     9: 'Part 3 alternating orientations',
  //     10: 'Part 1 & 2 both landscape',
  //     11: 'Part 1 landscape & Part 2 portrait',
  //     12: 'Part 1 & 2 both portrait',
  //     13: 'Part 1 portrait & Part 2 landscape',
  //     14: 'Part 1 & 3 both landscape',
  //     15: 'Part 1 landscape & Part 3 portrait',
  //     16: 'Part 1 & 3 both portrait',
  //     17: 'Part 1 portrait & Part 3 landscape',
  //     18: 'Part 2 & 3 both landscape',
  //     19: 'Part 2 landscape & Part 3 portrait',
  //     20: 'Part 2 & 3 both portrait',
  //     21: 'Part 2 portrait & Part 3 landscape'
  //   };

  //   return descriptions[testId] || `Test ${testId}`;
  // }
}
